import React from "react";
import { X, User, Users, Plug } from "lucide-react";

import { UserProfile } from "../types";

interface SettingsModalMobileProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  userProfile: UserProfile;
  onClose: () => void;
  onLogout: () => void;
  onDeleteAccount: () => void;
  getTeamColor: (teamId: string) => { style: string; dot: string };
  toggleTeam: (teamId: string) => void;
  // We pass the tab components themselves to avoid duplicate logic
  tabs: {
    profile: React.ReactNode;
    teams: React.ReactNode;
    integrations: React.ReactNode;
    shortcuts: React.ReactNode;
  };
}

const SettingsModalMobile: React.FC<SettingsModalMobileProps> = ({
  activeTab,
  setActiveTab,
  userProfile,
  onClose,
  tabs,
}) => {
  const hasTeams = userProfile.teams && userProfile.teams.length > 0;
  const tabConfig = [
    { id: "profile", label: "Profile", icon: User },
    ...(hasTeams
      ? [
          { id: "teams", label: "Teams", icon: Users },
          { id: "integrations", label: "Sync", icon: Plug },
        ]
      : []),
  ];

  return (
    <div
      className="fixed inset-0 z-[100] bg-white dark:bg-gray-900 flex flex-col animate-in slide-in-from-bottom duration-300"
      style={{
        paddingTop: "var(--safe-area-top)",
        paddingBottom: "var(--safe-area-bottom)",
      }}
    >
      {/* Mobile Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 shrink-0">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white uppercase tracking-tight">
          Settings
        </h2>
        <button
          onClick={onClose}
          className="p-2 bg-white dark:bg-gray-700 rounded-full shadow-sm border border-gray-200 dark:border-gray-600"
        >
          <X size={20} />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6 pb-24">
        {activeTab === "profile" && tabs.profile}
        {activeTab === "teams" && tabs.teams}
        {activeTab === "integrations" && tabs.integrations}
      </div>

      {/* Bottom Tab Bar */}
      <div
        className="fixed bottom-0 inset-x-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-t border-gray-200 dark:border-gray-800 p-3 flex justify-around items-center z-20 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]"
        style={{ paddingBottom: "calc(var(--safe-area-bottom) + 12px)" }}
      >
        {tabConfig.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-col items-center gap-1 transition-all ${
              activeTab === tab.id
                ? "text-brand-500 scale-110"
                : "text-gray-400"
            }`}
          >
            <tab.icon size={20} />
            <span className="text-[10px] font-bold uppercase tracking-tighter">
              {tab.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SettingsModalMobile;
